/**
 * businessSignupManifests.js
 * ──────────────────────────
 * One manifest per module-variant. Drives which fields the unified
 * <BusinessSignup> renders. Every field listed here is required (or
 * has its own optional validator declared in businessSignupFields).
 *
 * Sections render top-down in the order they appear in `sections`.
 *
 * `business_id_required`:
 *   true   → ABN/NZBN/EIN must be verified before submit
 *   false  → "I don't have a business ID" toggle is shown; opting out
 *            records the directory record as abn_status="not_required"
 *
 * `international`:
 *   true   → uses country/city/state_or_province/postal_code fields
 *            and hides AU-only validators. HustleConnect plugin is
 *            the only multi-country module.
 *   false  → AU-only fields (default)
 */

const COMMON_AGREEMENTS = ["terms", "marketing_optin"];

export const MANIFESTS = {
  // ── Plaza · co-op marketplace · AU only ──────────────────────────
  plaza_vendor: {
    id:        "plaza_vendor",
    module:    "plaza",
    title:     "Join HustlePlaza",
    subtitle:  "AU vendors — co-op marketplace",
    country:   "au",
    international: false,
    business_id_required: true,
    sections: [
      { id: "identity", title: "Your business",
        fields: ["business_name", "trading_name", "website_url"] },
      { id: "business_id", title: "Business identifier",
        fields: ["abn"] },
      { id: "contact", title: "Contact",
        fields: ["contact_first", "contact_last", "primary_email", "mobile"] },
      { id: "address", title: "Operating address",
        fields: ["address_line_1", "suburb", "state", "postcode"] },
      { id: "specifics", title: "What you sell",
        fields: ["vendor_category", "cuisine_or_type"] },
      { id: "agreements", title: "Agreements",
        fields: [...COMMON_AGREEMENTS, "co_op_membership"] },
    ],
  },

  // ── Plate · restaurant SaaS · AU only ────────────────────────────
  plate_restaurant: {
    id:        "plate_restaurant",
    module:    "plate",
    title:     "Onboard your restaurant",
    subtitle:  "HustlePlate — Australia",
    country:   "au",
    international: false,
    business_id_required: true,
    sections: [
      { id: "identity", title: "Your restaurant",
        fields: ["business_name", "trading_name", "website_url"] },
      { id: "business_id", title: "Business identifier",
        fields: ["abn"] },
      { id: "contact", title: "Owner contact",
        fields: ["contact_first", "contact_last", "primary_email", "mobile"] },
      { id: "address", title: "Venue address",
        fields: ["address_line_1", "address_line_2", "suburb", "state", "postcode"] },
      { id: "specifics", title: "Venue details",
        fields: ["cuisine_or_type", "seat_count", "liquor_licence"] },
      { id: "agreements", title: "Agreements",
        fields: COMMON_AGREEMENTS },
    ],
  },

  // ── HustleConnect plugin · AU ────────────────────────────────────
  plugin_au: {
    id:        "plugin_au",
    module:    "plugin",
    title:     "Install HustleConnect",
    subtitle:  "WordPress plugin · Australia",
    country:   "au",
    international: false,
    business_id_required: true,
    sections: [
      { id: "identity", title: "Your business",
        fields: ["business_name", "trading_name", "website_url"] },
      { id: "business_id", title: "Business identifier",
        fields: ["abn"] },
      { id: "contact", title: "Site admin",
        fields: ["contact_first", "contact_last", "primary_email", "mobile"] },
      { id: "address", title: "Business address",
        fields: ["address_line_1", "suburb", "state", "postcode"] },
      { id: "agreements", title: "Agreements",
        fields: COMMON_AGREEMENTS },
    ],
  },

  // ── HustleConnect plugin · International ─────────────────────────
  plugin_intl: {
    id:        "plugin_intl",
    module:    "plugin",
    title:     "Install HustleConnect",
    subtitle:  "WordPress plugin · International",
    country:   null,            // country is picked by the user
    international: true,
    business_id_required: false, // not all countries require business IDs
    sections: [
      { id: "country", title: "Where are you based?",
        fields: ["country"] },
      { id: "identity", title: "Your business",
        fields: ["business_name", "trading_name", "website_url"] },
      { id: "contact", title: "Site admin",
        fields: ["contact_first", "contact_last", "primary_email", "primary_phone"] },
      { id: "address", title: "Business address",
        fields: ["address_line_1", "city", "state_or_province", "postal_code"] },
      { id: "agreements", title: "Agreements",
        fields: COMMON_AGREEMENTS },
    ],
  },

  // ── Property · agency · AU ───────────────────────────────────────
  property_agency: {
    id:        "property_agency",
    module:    "property",
    title:     "Join HustleProperty",
    subtitle:  "Real-estate agency",
    country:   "au",
    international: false,
    business_id_required: true,
    sections: [
      { id: "listing_type", title: "I am listing as a",
        fields: ["property_listing_type"] },
      { id: "identity", title: "Your agency",
        fields: ["business_name", "trading_name", "website_url"] },
      { id: "business_id", title: "Business identifier",
        fields: ["abn"] },
      { id: "contact", title: "Primary contact",
        fields: ["contact_first", "contact_last", "primary_email", "mobile"] },
      { id: "address", title: "Agency address",
        fields: ["address_line_1", "suburb", "state", "postcode"] },
      { id: "agreements", title: "Agreements",
        fields: COMMON_AGREEMENTS },
    ],
  },

  // ── Property · private seller · AU ───────────────────────────────
  property_private: {
    id:        "property_private",
    module:    "property",
    title:     "List your property",
    subtitle:  "Private seller / landlord",
    country:   "au",
    international: false,
    business_id_required: false,  // private sellers don't need an ABN
    sections: [
      { id: "listing_type", title: "I am listing as a",
        fields: ["property_listing_type"] },
      { id: "identity", title: "About you",
        fields: ["full_name"] },
      { id: "business_id", title: "Business identifier",
        fields: ["abn"] },
      { id: "contact", title: "Contact",
        fields: ["primary_email", "mobile"] },
      { id: "address", title: "Property address",
        fields: ["address_line_1", "suburb", "state", "postcode"] },
      { id: "agreements", title: "Agreements",
        fields: COMMON_AGREEMENTS },
    ],
  },

  // ── Deals · voucher merchant · AU ────────────────────────────────
  deals_merchant: {
    id:        "deals_merchant",
    module:    "deals",
    title:     "Become a HustleDeals merchant",
    subtitle:  "Voucher & loyalty exchange",
    country:   "au",
    international: false,
    business_id_required: true,
    sections: [
      { id: "identity", title: "Your business",
        fields: ["business_name", "trading_name", "website_url"] },
      { id: "business_id", title: "Business identifier",
        fields: ["abn"] },
      { id: "contact", title: "Contact",
        fields: ["contact_first", "contact_last", "primary_email", "mobile"] },
      { id: "address", title: "Business address",
        fields: ["address_line_1", "suburb", "state", "postcode"] },
      { id: "specifics", title: "What deals do you run?",
        fields: ["vendor_category"] },
      { id: "agreements", title: "Agreements",
        fields: COMMON_AGREEMENTS },
    ],
  },

  // ── Chat · workspace owner · AU ──────────────────────────────────
  chat_workspace: {
    id:        "chat_workspace",
    module:    "chat",
    title:     "Set up HustleChat",
    subtitle:  "Workspace owner",
    country:   "au",
    international: false,
    business_id_required: true,
    sections: [
      { id: "identity", title: "Your business",
        fields: ["business_name", "website_url"] },
      { id: "business_id", title: "Business identifier",
        fields: ["abn"] },
      { id: "contact", title: "Workspace admin",
        fields: ["contact_first", "contact_last", "primary_email", "mobile"] },
      { id: "agreements", title: "Agreements",
        fields: COMMON_AGREEMENTS },
    ],
  },

  // ── Music · venue mode (ABN required) ────────────────────────────
  music_venue: {
    id:        "music_venue",
    module:    "music",
    variant:   "venue",
    title:     "Add your venue to HustleMusic",
    country:   "au",
    international: false,
    business_id_required: true,
    sections: [
      { id: "identity", title: "Your venue",
        fields: ["business_name", "trading_name"] },
      { id: "business_id", title: "Business identifier",
        fields: ["abn"] },
      { id: "contact", title: "Contact",
        fields: ["contact_first", "contact_last", "primary_email", "mobile"] },
      { id: "address", title: "Venue address",
        fields: ["address_line_1", "suburb", "state", "postcode"] },
      { id: "agreements", title: "Agreements",
        fields: COMMON_AGREEMENTS },
    ],
  },

  // ── Music · musician mode (no ABN required) ─────────────────────
  music_musician: {
    id:        "music_musician",
    module:    "music",
    variant:   "musician",
    title:     "Join HustleMusic",
    subtitle:  "Solo musician / band",
    country:   "au",
    international: false,
    business_id_required: false,
    sections: [
      { id: "identity", title: "About you",
        fields: ["full_name", "artist_genre", "website_url"] },
      { id: "contact", title: "Contact",
        fields: ["primary_email", "mobile"] },
      { id: "agreements", title: "Agreements",
        fields: COMMON_AGREEMENTS },
    ],
  },
};

export const getManifest = (id) => MANIFESTS[id];
